import { useEffect, useRef } from "react";
import { type Query, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import {
  type HomeNavigationStartedDetail,
  VIEWR_HOME_NAVIGATION_STARTED_EVENT,
} from "../../services/editor/editorSessionCleanup";
import { subscribeSavePersisted } from "../../services/editor/saveEvents";
import {
  invalidateFileListQueries,
  runHomeNavigationFinalFilesRefresh,
  syncViewrSavePersisted,
} from "../../services/query/fileCacheSync";
import { traceProjectThumbnail } from "../../utils/thumbnailTrace";
import { shouldTraceProjectThumbnailPersist } from "../../services/editor/saveUiEvents";

export const useServerStateEventBridge = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const previousUidRef = useRef(currentUser?.uid);

  useEffect(() => {
    const uid = previousUidRef.current;
    previousUidRef.current = currentUser?.uid;
    if (!uid || uid === currentUser?.uid) return;
    const filters = {
      predicate: (query: Query) => query.queryKey[1] === uid,
    };
    void queryClient.cancelQueries(filters);
    queryClient.removeQueries(filters);
  }, [currentUser?.uid, queryClient]);

  useEffect(() => {
    const handleUploadWorkCompleted = () => {
      invalidateFileListQueries({
        queryClient,
        uid: null,
        includeRaw: true,
        includeConverted: true,
        includeLibrary: true,
        includeStorageUsage: true,
      });
    };

    const handleViewrPersisted = (event: {
      detail?: {
        gcsPath?: string;
        skipThumbnailAfterPersist?: boolean;
        reason?: string;
      };
    }) => {
      const detail = event.detail;
      if (
        shouldTraceProjectThumbnailPersist({
          skipThumbnailAfterPersist: detail?.skipThumbnailAfterPersist,
          reason: detail?.reason,
        })
      ) {
        traceProjectThumbnail("save_persisted_event", {
          gcsPath: detail?.gcsPath,
          skipThumbnailAfterPersist:
            detail?.skipThumbnailAfterPersist === true,
          reason: detail?.reason,
        });
      }
      syncViewrSavePersisted({
        queryClient,
        uid: currentUser?.uid,
        gcsPath: detail?.gcsPath,
        // Figma/XR autosave only skips a new Unity screenshot capture. The
        // existing project sidecar still needs to be re-signed and applied to
        // Recent so a placeholder does not remain cached.
      });
    };

    const handleHomeNavigationStarted = (
      event: Event | CustomEvent<HomeNavigationStartedDetail>,
    ) => {
      if (!currentUser?.token) return;
      const detail = (event as CustomEvent<HomeNavigationStartedDetail>).detail;
      void runHomeNavigationFinalFilesRefresh({
        queryClient,
        uid: currentUser?.uid,
        viewrGcsPath: detail?.viewrGcsPath,
        includeSamples: true,
      });
    };

    window.addEventListener("upload:workCompleted", handleUploadWorkCompleted);
    window.addEventListener(
      VIEWR_HOME_NAVIGATION_STARTED_EVENT,
      handleHomeNavigationStarted,
    );
    const unsubscribeSavePersisted = subscribeSavePersisted(handleViewrPersisted);

    return () => {
      window.removeEventListener(
        "upload:workCompleted",
        handleUploadWorkCompleted,
      );
      window.removeEventListener(
        VIEWR_HOME_NAVIGATION_STARTED_EVENT,
        handleHomeNavigationStarted,
      );
      unsubscribeSavePersisted();
    };
  }, [currentUser?.uid, currentUser?.token, queryClient]);
};
